import { useMemo } from 'react'

import { useSelector } from 'react-redux'
import { faClock, faComment, faSchool, faUserTie } from '@fortawesome/pro-solid-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isNonEmptyArray } from '../../../../utils'
import { EUROPEAN_DATE } from '../../../../utils/constants'
import { getTimezoneLessMoment } from '../../../../utils/momentjs'
import { getCoordinatorFullname } from '../../../../utils/entities/schoolPortalRequest'

const EMPTY = '—'

const orEmpty = value => (value ?? '') === '' ? EMPTY : value

const formatPeriod = period => {
  const startDate = getTimezoneLessMoment(period?.startDate)
  const endDate = getTimezoneLessMoment(period?.endDate)

  return (startDate === null || endDate === null)
    ? EMPTY
    : `${startDate.format(EUROPEAN_DATE)} → ${endDate.format(EUROPEAN_DATE)}`
}

const sectionLabel = line => orEmpty(
  [line.section?.abbreviation, line.sectionToYear?.schoolYearName].filter(part => (part ?? '') !== '').join(' — ')
)

const IdentityCard = ({ icon, title, children }) => (
  <div className='identity'>
    <div className='identity-title'><FontAwesomeIcon icon={icon} /> {title}</div>
    {children}
  </div>
)

const SchoolPortalSummary = ({ areas = [], coordinator, countries = [], institutionName, requests = [], school }) => {
  const t = useSelector(selectTranslate)

  const countryName = useMemo(() => {
    const country = countries.find(c => getIdOrNull(c) === getIdOrNull(school?.country))

    return country ? t('country.' + country.code) : null
  }, [countries, school?.country, t])

  const areaName = useMemo(() => {
    const area = areas.find(a => getIdOrNull(a) === getIdOrNull(school?.area))

    return area ? area.localName : null
  }, [areas, school?.area])

  const totalPlaces = useMemo(() => requests.reduce((total, line) => total + (line.total ?? 0), 0), [requests])

  const schoolOrigin = [areaName, countryName].filter(part => (part ?? '') !== '').join(' · ')
  const schoolContact = [school?.email, school?.phoneNumber].filter(part => (part ?? '') !== '').join(' · ')

  return (
    <div className='pd-12 portal-summary'>
      <div className='headline'>
        <div className='headline-figure'>{totalPlaces}</div>
        <div>
          <div className='headline-title'>{t('school_portal.summary.headline', { institution: orEmpty(institutionName) })}</div>
          <div className='headline-detail'>
            {t('school_portal.summary.headline_detail', { lines: requests.length })}
          </div>
        </div>
      </div>

      <div className='table-wrapper'>
        <table className='requests'>
          <thead>
            <tr>
              <th>{t('form.label.section')}</th>
              <th>{t('form.label.sector')}</th>
              <th>{t('form.label.periods')}</th>
              <th className='numeric'>{t('school_portal.requests.places')}</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((line, index) => {
              const extras = [
                (line.duration ?? null) === null ? null : { icon: faClock, text: t('school_portal.summary.duration', { hours: line.duration }) },
                (line.comment ?? '') === '' ? null : { icon: faComment, text: line.comment }
              ].filter(extra => extra !== null)

              return [
                <tr key={`line-${index}`} className={isNonEmptyArray(extras) ? 'no-border' : ''}>
                  <td data-label={t('form.label.section')}><b>{sectionLabel(line)}</b></td>
                  <td data-label={t('form.label.sector')}>{orEmpty(line.sector?.name)}</td>
                  <td className='periods' data-label={t('form.label.periods')}>
                    {isNonEmptyArray(line.requestPeriods)
                      ? line.requestPeriods.map((period, i) => <div key={`period-${i}`}>{formatPeriod(period)}</div>)
                      : EMPTY}
                  </td>
                  <td className='numeric' data-label={t('school_portal.requests.places')}><b>{line.total ?? EMPTY}</b></td>
                </tr>,
                isNonEmptyArray(extras) && (
                  <tr key={`extras-${index}`} className='extras'>
                    <td colSpan={4}>
                      {extras.map((extra, i) => (
                        <span key={`extra-${i}`}><FontAwesomeIcon icon={extra.icon} /> {extra.text}</span>
                      ))}
                    </td>
                  </tr>
                )
              ]
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>{t('school_portal.summary.total', { lines: requests.length })}</td>
              <td className='numeric'>{totalPlaces}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className='identities'>
        <IdentityCard icon={faUserTie} title={t('school_portal.summary.coordinator')}>
          <div className='strong'>{orEmpty(getCoordinatorFullname(coordinator))}</div>
          <div>{orEmpty(coordinator?.jobTitle)}</div>
          <div className='muted'>{orEmpty(coordinator?.email)}</div>
          {(coordinator?.phone ?? '') !== '' && <div className='muted'>{coordinator.phone}</div>}
        </IdentityCard>

        <IdentityCard icon={faSchool} title={t('school_portal.summary.school')}>
          <div className='strong'>
            {orEmpty(school?.name)}
            {(school?.abbreviation ?? '') !== '' && <span className='muted'> ({school.abbreviation})</span>}
          </div>
          <div>{orEmpty(school?.address)}</div>
          {schoolOrigin !== '' && <div className='muted'>{schoolOrigin}</div>}
          {schoolContact !== '' && <div className='muted'>{schoolContact}</div>}
        </IdentityCard>
      </div>

      <p className='footnote'><FontAwesomeIcon icon={faInfoCircle} /> {t('school_portal.summary.footnote')}</p>
    </div>
  )
}

export default SchoolPortalSummary
